import { Link } from "@tanstack/react-router";
import {
  Monitor,
  TrendingUp,
  MapPin,
  Target,
  ArrowRight,
  Phone,
  type LucideIcon,
} from "lucide-react";

type ServiceKey = "web-design" | "seo-services" | "local-seo" | "google-ads-ppc";

type ServiceLink = {
  key: ServiceKey;
  to: string;
  label: string;
  anchor: string;
  desc: string;
  Icon: LucideIcon;
};

const ALL_SERVICES: ServiceLink[] = [
  {
    key: "web-design",
    to: "/colorado-springs-web-design",
    label: "Colorado Web Design",
    anchor: "Colorado web design services",
    desc: "Custom, mobile-first websites engineered to convert local visitors into booked jobs.",
    Icon: Monitor,
  },
  {
    key: "seo-services",
    to: "/colorado-springs-seo",
    label: "Colorado SEO",
    anchor: "Colorado SEO services",
    desc: "Rank on page one of Google for the searches your customers actually use.",
    Icon: TrendingUp,
  },
  {
    key: "local-seo",
    to: "/colorado-springs-google-maps",
    label: "Google Maps Ranking",
    anchor: "Google Maps & local SEO",
    desc: "Dominate the Map Pack so nearby customers call you first.",
    Icon: MapPin,
  },
  {
    key: "google-ads-ppc",
    to: "/colorado-springs-ppc",
    label: "Google Ads / PPC",
    anchor: "Google Ads management",
    desc: "Profitable paid search campaigns with transparent reporting and zero contracts.",
    Icon: Target,
  },
];

/**
 * Cross-link block surfaced on every service page. Lists the *other* services
 * (filtered by `current`) and a strong CTA link to /contact for conversions
 * and tighter crawl paths between money pages.
 */
export function RelatedServicesLinks({
  current,
  className,
}: {
  current: ServiceKey;
  className?: string;
}) {
  const related = ALL_SERVICES.filter((s) => s.key !== current);

  return (
    <section
      aria-label="Related services and contact"
      className={`bg-secondary/40 py-20 lg:py-24 ${className ?? ""}`}
    >
      <div className="mx-auto max-w-6xl px-5 lg:px-8">
        <div className="text-center">
          <div className="text-xs font-bold uppercase tracking-[0.18em] text-brand-gold-deep">
            Explore more
          </div>
          <h2 className="mt-2 text-3xl font-extrabold text-foreground sm:text-4xl">
            Related services that pair with this one
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-base text-muted-foreground">
            Most Colorado businesses we work with combine two or three of these.
            Browse the related services below — or skip ahead and{" "}
            <Link
              to="/contact"
              className="font-semibold text-brand-gold-deep underline underline-offset-4 hover:text-brand-gold"
            >
              talk to the owner directly
            </Link>
            .
          </p>
        </div>

        <ul className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {related.map(({ to, label, anchor, desc, Icon }) => (
            <li key={to}>
              <Link
                to={to}
                aria-label={anchor}
                className="group flex h-full flex-col rounded-2xl border border-border bg-card p-6 shadow-card transition-spring hover:-translate-y-1 hover:border-brand-gold hover:shadow-glow"
              >
                <span className="grid h-11 w-11 place-items-center rounded-lg bg-brand-gold/10 text-brand-gold-deep">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-4 text-lg font-extrabold text-foreground group-hover:text-brand-gold-deep">
                  {label}
                </h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">
                  {desc}
                </p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-brand-gold-deep">
                  Explore {anchor}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            </li>
          ))}
        </ul>

        <div className="mt-10 rounded-2xl border border-brand-gold/30 bg-card p-6 text-center shadow-elegant sm:p-8">
          <h3 className="text-2xl font-extrabold text-foreground sm:text-3xl">
            Not sure which service you need?
          </h3>
          <p className="mx-auto mt-2 max-w-xl text-sm text-muted-foreground">
            Tell us about your business in 30 seconds. We'll point you to the
            right mix — even if that means recommending a competitor.
          </p>
          <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              to="/contact"
              className="group inline-flex h-12 items-center justify-center gap-2 rounded-md bg-gradient-gold px-7 text-sm font-bold uppercase tracking-wider text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
            >
              Contact our Colorado team
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <a
              href="tel:+17194193935"
              className="inline-flex h-12 items-center justify-center gap-2 rounded-md border border-border bg-background px-7 text-sm font-bold uppercase tracking-wider text-foreground transition-smooth hover:border-brand-gold hover:text-brand-gold-deep"
             aria-label="Call (719) 419-3935">
              <Phone className="h-4 w-4" />
              (719) 419-3935
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
